import { useState, useEffect } from 'react'
import { Smartphone, Plus, Trash2, Loader2, AlertTriangle, XCircle } from 'lucide-react'
import { useProxyConfig } from '@/hooks/useProxyConfig'

interface TotpDevice {
  id: string
  name: string
  createdAt: string
}

interface AddDeviceFlowProps {
  onClose: () => void
  onAdded: () => void
}

function AddDeviceFlow({ onClose, onAdded }: AddDeviceFlowProps) {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const headers: Record<string, string> = {
    ...(proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}),
    'Content-Type': 'application/json',
  }

  type Step = 'name' | 'qr' | 'confirm'
  const [step, setStep] = useState<Step>('name')
  const [name, setName] = useState('')
  const [deviceId, setDeviceId] = useState('')
  const [secret, setSecret] = useState('')
  const [qrBase64, setQrBase64] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleGenerate() {
    if (!proxyUrl) return
    setLoading(true); setError(null)
    try {
      const params = name.trim() ? `?name=${encodeURIComponent(name.trim())}` : ''
      const r = await fetch(`${proxyUrl}/local/provider-auth/setup${params}`, { headers })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json() as { deviceId: string; secret: string; qr_base64: string; name: string }
      setDeviceId(data.deviceId)
      setSecret(data.secret)
      setQrBase64(data.qr_base64)
      setStep('qr')
    } catch {
      setError('Error al generar el QR')
    } finally {
      setLoading(false)
    }
  }

  async function handleConfirm() {
    if (!proxyUrl || code.length !== 6) return
    setLoading(true); setError(null)
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/setup/confirm`, {
        method: 'POST', headers,
        body: JSON.stringify({ deviceId, secret, name: name.trim() || 'Dispositivo', code }),
      })
      const data = await r.json() as { success?: boolean; error?: string }
      if (data.success) {
        onAdded()
      } else {
        setError(data.error ?? 'Código incorrecto')
        setCode('')
      }
    } catch {
      setError('Error de red')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-white font-medium text-sm">Agregar dispositivo TOTP</p>
        <button onClick={onClose} className="text-white/30 hover:text-white/70 text-xs transition-colors">Cancelar</button>
      </div>

      {step === 'name' && (
        <>
          <div className="flex flex-col gap-1.5">
            <label className="text-white/40 text-xs font-medium uppercase tracking-wide">Nombre del dispositivo</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGenerate()}
              placeholder="ej. iPhone Juan"
              maxLength={40}
              className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition-all"
            />
          </div>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-40 active:scale-[0.98] transition-all"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Generar QR
          </button>
        </>
      )}

      {step === 'qr' && (
        <>
          <div className="flex justify-center">
            <div className="bg-white p-3 rounded-xl shadow-xl">
              <img src={qrBase64} alt="QR TOTP" className="w-40 h-40 block" />
            </div>
          </div>
          <p className="text-white/40 text-xs text-center">
            Escanea con tu app autenticadora. Aparecerá como <strong className="text-white/70">{name || 'Proveedor'}</strong>.
          </p>
          <button
            onClick={() => { setCode(''); setStep('confirm') }}
            className="w-full py-2.5 rounded-xl text-white text-sm font-semibold active:scale-[0.98] transition-all"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
          >
            Ya lo escaneé → Confirmar
          </button>
        </>
      )}

      {step === 'confirm' && (
        <>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={e => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(null) }}
            onKeyDown={e => e.key === 'Enter' && code.length === 6 && handleConfirm()}
            placeholder="000 000"
            className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-center text-2xl font-mono text-white tracking-[0.4em] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 transition-all ${error ? 'border-red-500/60' : 'border-white/10'}`}
          />
          <button
            onClick={handleConfirm}
            disabled={code.length !== 6 || loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-40 active:scale-[0.98] transition-all"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirmar dispositivo
          </button>
          <button onClick={() => setStep('qr')} className="text-center text-xs text-white/35 hover:text-white/70 transition-colors">
            ← Ver QR de nuevo
          </button>
        </>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <XCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  )
}

export function ProviderTotpManager() {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const headers: Record<string, string> = proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}

  const [devices, setDevices] = useState<TotpDevice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [showAdd, setShowAdd] = useState(false)

  useEffect(() => { loadDevices() }, [proxyUrl])

  async function loadDevices() {
    if (!proxyUrl) return
    setLoading(true)
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/devices`, { headers })
      const data = await r.json() as { devices: TotpDevice[] }
      setDevices(data.devices)
    } catch {
      setError('Error al cargar dispositivos')
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete(id: string) {
    if (!proxyUrl) return
    setDeleting(id); setDeleteError(null)
    try {
      const r = await fetch(`${proxyUrl}/local/provider-auth/devices/${id}`, {
        method: 'DELETE', headers,
      })
      const data = await r.json() as { success?: boolean; error?: string }
      if (data.success) {
        await loadDevices()
      } else {
        setDeleteError(data.error ?? 'Error al revocar')
      }
    } catch {
      setDeleteError('Error de red')
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <XCircle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}
      {deleteError && (
        <div className="flex items-center gap-2 text-amber-400 text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5" />
          {deleteError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-white/30" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {devices.map(d => (
            <div key={d.id} className="flex items-center gap-3 bg-white/5 border border-white/8 rounded-xl px-4 py-3">
              <Smartphone className="w-4 h-4 text-white/30 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{d.name}</p>
                <p className="text-white/30 text-xs">Agregado {new Date(d.createdAt).toLocaleDateString()}</p>
              </div>
              <button
                onClick={() => handleDelete(d.id)}
                disabled={deleting === d.id}
                title="Revocar dispositivo"
                className="p-1.5 text-white/25 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-30"
              >
                {deleting === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            </div>
          ))}
        </div>
      )}

      {showAdd ? (
        <AddDeviceFlow onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); loadDevices() }} />
      ) : (
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-white/60 text-sm font-medium border border-white/10 hover:bg-white/8 hover:text-white transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Agregar dispositivo TOTP
        </button>
      )}
    </div>
  )
}
